// Lanza la mini guía de las novedades del ERP (catálogo en ./novedades.js),
// una sola vez por persona. Se monta una vez en App.js (dentro de GuiaProvider)
// y no pinta nada: usa el motor de la guía con `novedad: true`. El motor vive
// en Layout (GuiaInteractiva) y, en las pantallas sin Layout como la ficha de
// presupuesto, en GuiaSinLayout.
//
// - Las novedades vistas se leen del servidor una vez por sesión de página y
//   por token (preferencia `novedades_vistas`, GET/PUT /api/preferencias) en un
//   almacén de módulo, con espejo en localStorage['novedades_vistas'] (por
//   usuario) por si el servidor falla. Lo que solo estaba en el espejo se sube
//   al servidor al leerlo (el PUT suma, nunca sustituye).
// - Tras cada cambio de pantalla mira cada segundo, durante 15 s como mucho,
//   si alguna novedad no vista aplica en la pantalla; lanza la primera y nada
//   más en esta carga de página. Al abrir un modal o un panel con una novedad
//   (sin cambiar de ruta), revisarNovedades() vuelve a abrir esa ventana.
// - Nunca con una guía en marcha, con la de bienvenida pendiente o pospuesta,
//   en la simulación de vista de empleado ni sin sesión.
// - Al terminarla ("Entendido" o "Cerrar") se marca vista al instante en este
//   navegador y se guarda en el servidor. Si la persona se va a otra pantalla
//   con la novedad abierta, se cierra y también cuenta como vista.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthToken, preferenciasAPI } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { CLAVE_TOUR_POSPUESTO, useGuia } from '../../context/GuiaContext';
import { useVistaEmpleado } from '../../context/VistaEmpleadoContext';
import { registrarAccion } from '../../utils/usoErp';
import { buscar } from './GuiaInteractiva';
import { EVENTO_REVISAR_NOVEDADES, NOVEDADES } from './novedades';

const CLAVE_LOCAL = 'novedades_vistas';
const MAX_VISTAS = 200;
const INTERVALO = 1000; // ms entre comprobaciones tras un cambio de pantalla
const VENTANA = 15000; // ms que se sigue mirando tras cada cambio de pantalla

const idUsuario = (user) => (user ? String(user.id || user.email || user.empleado_id || '') : '');

const leerToken = () => {
  try {
    return getAuthToken() || null;
  } catch (e) {
    return null;
  }
};

// Espejo local: { [usuario]: [ids] } (el navegador puede ser compartido)
const leerMapaLocal = () => {
  try {
    const mapa = JSON.parse(window.localStorage.getItem(CLAVE_LOCAL) || '{}');
    return mapa && typeof mapa === 'object' && !Array.isArray(mapa) ? mapa : {};
  } catch (e) {
    return {};
  }
};

const leerLocal = (usuario) => {
  const lista = leerMapaLocal()[usuario];
  return Array.isArray(lista) ? lista.filter((id) => typeof id === 'string' && id) : [];
};

const escribirLocal = (usuario, ids) => {
  try {
    const mapa = leerMapaLocal();
    mapa[usuario] = ids.slice(-MAX_VISTAS);
    window.localStorage.setItem(CLAVE_LOCAL, JSON.stringify(mapa));
  } catch (e) {
    // Sin localStorage: solo en memoria y en el servidor
  }
};

// Tour de bienvenida pendiente o pospuesto: las novedades esperan
const tourPendienteOPospuesto = () => {
  try {
    if (window.sessionStorage.getItem(CLAVE_TOUR_POSPUESTO) === '1') return true;
  } catch (e) {
    // sin sessionStorage
  }
  try {
    if (!window.localStorage.getItem('empleado_token')) return false;
    const info = JSON.parse(window.localStorage.getItem('empleado_info') || 'null');
    return !!info && info.tour_pendiente === true && !info.tour_visto_en;
  } catch (e) {
    return false;
  }
};

const almacen = {
  usuario: null,
  vistas: new Set(),
  // Lectura del servidor en curso o hecha, y el token con el que se hizo
  carga: null,
  tokenCarga: null,
  // Una sola novedad por carga de página
  lanzada: false,
};

const cargarVistas = (usuario) => {
  const token = leerToken();
  if (!token || !usuario) return Promise.resolve(false);
  if (almacen.carga && almacen.tokenCarga === token && almacen.usuario === usuario) return almacen.carga;
  almacen.tokenCarga = token;
  almacen.usuario = usuario;
  almacen.vistas = new Set(leerLocal(usuario));
  almacen.carga = preferenciasAPI
    .obtener()
    .then((res) => {
      if (almacen.tokenCarga !== token || almacen.usuario !== usuario) return false;
      const servidor = Array.isArray(res?.data?.novedades_vistas) ? res.data.novedades_vistas : [];
      const soloLocal = [...almacen.vistas].filter((id) => !servidor.includes(id));
      servidor.forEach((id) => almacen.vistas.add(id));
      escribirLocal(usuario, [...almacen.vistas]);
      if (soloLocal.length > 0) {
        preferenciasAPI.guardar({ novedades_vistas: soloLocal }).catch((e) => {
          console.warn('No se han podido subir al servidor las novedades vistas en este navegador.', e);
        });
      }
      return true;
    })
    .catch((e) => {
      console.warn('No se han podido leer las novedades vistas del servidor; se usan las de este navegador.', e);
      return true;
    });
  return almacen.carga;
};

const marcarVista = (usuario, id) => {
  if (!usuario || !id || almacen.usuario !== usuario) return;
  almacen.vistas.add(id);
  escribirLocal(usuario, [...almacen.vistas]);
  if (leerToken()) {
    preferenciasAPI.guardar({ novedades_vistas: [id] }).catch((e) => {
      console.warn('No se ha podido guardar la novedad vista en el servidor; queda en este navegador.', e);
    });
  }
  try {
    registrarAccion('novedad.vista', id);
  } catch (e) {
    // El rastreo de uso nunca rompe nada
  }
};

// Pasos informativos para el motor: del segundo en adelante, si la zona no
// aparece en 2 s, la tarjeta sale como texto sin resaltar
const pasosDe = (novedad) => novedad.pasos.map((p, i) => ({
  clave: `novedad:${novedad.id}:${i}`,
  modulo: null,
  ruta: null,
  menu: null,
  selector: p.selector || null,
  accion: 'observar',
  esperar: null,
  opcional: i > 0,
  textoSiFalta: i > 0 ? p.texto : null,
  requiere: null,
  enMenu: false,
  posicion: p.posicion || 'auto',
  titulo: p.titulo,
  texto: p.texto,
  final: false,
}));

const aplicaAhora = (novedad, ruta) => {
  try {
    return !!novedad.aplica({ ruta, buscar: (selector) => !!buscar(selector) });
  } catch (e) {
    return false;
  }
};

const NovedadesPendientes = () => {
  const { user } = useAuth();
  const {
    estado, guiaActiva, hayGuiaPospuesta, iniciarGuia, pararGuia,
  } = useGuia();
  const { simulando } = useVistaEmpleado();
  const location = useLocation();
  const usuario = idUsuario(user);
  const ruta = location.pathname;
  const busqueda = location.search;
  const [cargado, setCargado] = useState(false);
  // Novedad abierta ahora y pantalla en la que se abrió
  const activa = useRef(null);
  // Avisos de revisarNovedades() (un modal recién abierto)
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const alRevisar = () => setRevision((n) => n + 1);
    window.addEventListener(EVENTO_REVISAR_NOVEDADES, alRevisar);
    return () => window.removeEventListener(EVENTO_REVISAR_NOVEDADES, alRevisar);
  }, []);

  useEffect(() => {
    setCargado(false);
    if (!usuario) return undefined;
    let vivo = true;
    cargarVistas(usuario).then((ok) => {
      if (vivo && ok) setCargado(true);
    });
    return () => {
      vivo = false;
    };
  }, [usuario]);

  const terminarNovedad = useCallback((id) => {
    activa.current = null;
    marcarVista(usuario, id);
  }, [usuario]);

  const bloqueado = !usuario || !cargado || simulando || guiaActiva || hayGuiaPospuesta;

  // Tras cada cambio de pantalla (o al quedar libre), buscar una novedad que aplique
  useEffect(() => {
    if (bloqueado || almacen.lanzada) return undefined;
    const inicio = Date.now();
    let intervalo = null;
    const intentar = () => {
      if (almacen.lanzada || Date.now() - inicio > VENTANA) {
        if (intervalo !== null) clearInterval(intervalo);
        intervalo = null;
        return;
      }
      if (tourPendienteOPospuesto()) return;
      const pendiente = NOVEDADES.find((n) => !almacen.vistas.has(n.id) && aplicaAhora(n, ruta));
      if (!pendiente) return;
      almacen.lanzada = true;
      activa.current = { id: pendiente.id, ruta };
      iniciarGuia({
        pasos: pasosDe(pendiente),
        nombre: `novedad:${pendiente.id}`,
        novedad: true,
        alTerminar: () => terminarNovedad(pendiente.id),
      });
    };
    intentar();
    if (!almacen.lanzada) intervalo = setInterval(intentar, INTERVALO);
    return () => {
      if (intervalo !== null) clearInterval(intervalo);
    };
    // `busqueda` vuelve a abrir la ventana al cambiar de pestaña por la URL y
    // `revision` al abrir un modal que avisa con revisarNovedades()
  }, [bloqueado, ruta, busqueda, revision, iniciarGuia, terminarNovedad]);

  // Con la novedad abierta: si cambia de pantalla, se cierra y cuenta como
  // vista; si empieza la simulación, se cierra sin marcarla
  const novedadAbierta = !!estado?.novedad;
  useEffect(() => {
    const abierta = activa.current;
    if (!novedadAbierta || !abierta) return;
    if (simulando) {
      activa.current = null;
      pararGuia();
      return;
    }
    if (ruta !== abierta.ruta) {
      terminarNovedad(abierta.id);
      pararGuia();
    }
  }, [novedadAbierta, ruta, simulando, pararGuia, terminarNovedad]);

  return null;
};

export default NovedadesPendientes;
